import { formatDateToDateString } from "@components/inputs/date/utils";
import { IEntity } from "@odata/BindingContext";
import {
    EntitySetName,
    IPrAnnualTaxSettlementRequestEntity,
    IPrEmploymentEntity,
    PrAnnualTaxSettlementRequestEntity,
    PrEmploymentEntity
} from "@odata/GeneratedEntityTypes";
import { IBatchResult } from "@odata/OData";
import { ODataQueryResult } from "@odata/ODataParser";
import { IPrAnnualTaxSettlementRequestCustomData } from "@pages/payroll/annualTaxSettlementRequest/AnnualTaxSettlementRequestFormView";
import { logger } from "@utils/log";

import { getUtcDateBy, getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";

export async function loadPossibleEmployeesForNewAnnualTaxSettlement(storage: FormStorage<IEntity, IPrAnnualTaxSettlementRequestCustomData>, year: number): Promise<void> {
    const yearDate = getUtcDayjs(getUtcDateBy(year));
    const yearStart = formatDateToDateString(yearDate.startOf("year"));
    const yearEnd = formatDateToDateString(yearDate.endOf("year"));

    try {
        const batch = storage.oData.batch();
        batch.beginAtomicityGroup("group1");

        batch.getEntitySetWrapper(EntitySetName.PrEmployments)
            .query()
            .filter(`(${PrEmploymentEntity.DateEnd} eq null or ${PrEmploymentEntity.DateEnd} ge ${yearStart}) and ${PrEmploymentEntity.DateStart} le ${yearEnd}`)
            .select("Id")
            .expand(PrEmploymentEntity.Employee, (q) => q.select("Id"));

        batch.getEntitySetWrapper(EntitySetName.PrAnnualTaxSettlementRequests)
            .query()
            .filter(`${PrAnnualTaxSettlementRequestEntity.Year} eq ${year}`)
            .select("Id")
            .expand(PrAnnualTaxSettlementRequestEntity.Employee, (q) => q.select("Id"));

        batch.getEntitySetWrapper(EntitySetName.PrAnnualTaxSettlements)
            .query()
            .filter(`${PrAnnualTaxSettlementRequestEntity.Year} eq ${year}`)
            .select("Id")
            .expand(PrAnnualTaxSettlementRequestEntity.Employee, (q) => q.select("Id"));

        const results = await batch.execute() as IBatchResult<ODataQueryResult>[];

        const employments = results[0].body.value as IPrEmploymentEntity[];
        const requests = results[1].body.value as IPrAnnualTaxSettlementRequestEntity[];
        const settlements = results[2].body.value as IPrAnnualTaxSettlementRequestEntity[];

        const possibleEmployeesIds: Set<number> = new Set(
            employments.map((employment: IPrEmploymentEntity) => {
                return employment.Employee.Id;
            })
        );

        // remove employees that already have a request or settlement for the year
        [...requests, ...settlements].forEach((request: IPrAnnualTaxSettlementRequestEntity) => {
            possibleEmployeesIds.delete(request.Employee.Id);
        });

        storage.setCustomData({ possibleEmployeesIds });
    } catch (error) {
        storage.setCustomData({ possibleEmployeesIds: new Set() });
        logger.error("Failed to fetch possible employees for Annual Tax Settlement Request", error);
    }

    storage.refresh();
}